import { useEffect, useState, useCallback } from 'react';
import {
  Bell,
  Shield,
  CheckCircle,
  AlertCircle,
  X,
  Save,
  Plus,
  Trash2,
  Mail,
  Zap,
  Pencil,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { NotificationPreference, NotificationRule } from '../types';
import api from '../services/api';
import {
  PRODUCT_LINES,
  PRODUCT_LINE_TO_DEVICES,
  ProductLineKey,
  getProductLineLabel,
} from '../constants/products';

const AVAILABLE_MARKETS = ['US', 'EU', 'UK', 'Canada', 'Australia'];

export default function NotificationsPage() {
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'super_user';

  const [activeTab, setActiveTab] = useState<'preferences' | 'rules'>('preferences');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [successBanner, setSuccessBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // User Preferences State
  const [preferences, setPreferences] = useState<NotificationPreference>({
    id: '',
    user_id: '',
    digest_frequency: 'daily',
    critical_alerts_enabled: true,
    preferred_markets: ['US', 'EU'],
    preferred_products: [],
    preferred_product_lines: [],
    notification_email: null,
  });

  // Admin Rules State
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [showAddRuleModal, setShowAddRuleModal] = useState<boolean>(false);
  const [newRuleName, setNewRuleName] = useState<string>('');
  const [newRuleSeverity, setNewRuleSeverity] = useState<string>('critical');
  const [newRuleEmails, setNewRuleEmails] = useState<string>('');
  const [editingRule, setEditingRule] = useState<NotificationRule | null>(null);
  const [ruleEnabled, setRuleEnabled] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const prefData = await api.notifications.getPreferences();
      setPreferences(prefData);

      if (isAdmin) {
        const rulesData = await api.notifications.listRules();
        setRules(rulesData);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to fetch notification settings';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSavePreferences = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      const updated = await api.notifications.updatePreferences({
        digest_frequency: preferences.digest_frequency,
        critical_alerts_enabled: preferences.critical_alerts_enabled,
        preferred_markets: preferences.preferred_markets,
        preferred_products: preferences.preferred_products,
        preferred_product_lines: preferences.preferred_product_lines,
        notification_email: preferences.notification_email,
      });
      setPreferences(updated);
      setSuccessBanner('Notification preferences saved successfully.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save preferences.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMarket = (market: string) => {
    setPreferences((prev) => {
      const exists = prev.preferred_markets.includes(market);
      return {
        ...prev,
        preferred_markets: exists
          ? prev.preferred_markets.filter((m) => m !== market)
          : [...prev.preferred_markets, market],
      };
    });
  };

  const handleToggleProduct = (prod: string) => {
    setPreferences((prev) => {
      const exists = prev.preferred_products.includes(prod);
      return {
        ...prev,
        preferred_products: exists
          ? prev.preferred_products.filter((p) => p !== prod)
          : [...prev.preferred_products, prod],
      };
    });
  };

  const handleToggleProductLine = (key: ProductLineKey) => {
    setPreferences((prev) => {
      const exists = prev.preferred_product_lines.includes(key);
      const preferred_product_lines = exists
        ? prev.preferred_product_lines.filter((p) => p !== key)
        : [...prev.preferred_product_lines, key];
      const devices = PRODUCT_LINE_TO_DEVICES[key];
      const preferred_products = exists
        ? prev.preferred_products.filter((p) => !devices.includes(p))
        : Array.from(new Set([...prev.preferred_products, ...devices]));
      return {
        ...prev,
        preferred_product_lines,
        preferred_products,
      };
    });
  };

  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newRuleName) return;

    const emails = newRuleEmails
      .split(',')
      .map((em) => em.trim())
      .filter((em) => em.length > 0);

    try {
      setError(null);
      const newRule = await api.notifications.createRule({
        name: newRuleName,
        severity_threshold: newRuleSeverity,
        recipient_emails: emails,
        enabled: true,
      });

      setRules((prev) => [...prev, newRule]);
      setShowAddRuleModal(false);
      setNewRuleName('');
      setNewRuleEmails('');
      setSuccessBanner(`Notification rule "${newRule.name}" created.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to create rule.';
      setError(msg);
    }
  };

  const [sendingTest, setSendingTest] = useState<boolean>(false);
  const [triggeringDigest, setTriggeringDigest] = useState<boolean>(false);

  const handleSendTestEmail = async () => {
    try {
      setSendingTest(true);
      setError(null);
      const res = await api.notifications.sendTestEmail();
      setSuccessBanner(res.message || 'Test regulatory alert email sent successfully.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send test email.';
      setError(msg);
    } finally {
      setSendingTest(false);
    }
  };

  const handleTriggerDigest = async (cadence: 'daily' | 'weekly') => {
    try {
      setTriggeringDigest(true);
      setError(null);
      const res = await api.notifications.triggerDigest(cadence);
      setSuccessBanner(res.message || `${cadence} digest triggered successfully.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to trigger digest run.';
      setError(msg);
    } finally {
      setTriggeringDigest(false);
    }
  };

  const handleDeleteRule = async (id: string, name: string) => {
    if (!window.confirm(`Delete rule "${name}"?`)) return;
    try {
      await api.notifications.deleteRule(id);
      setRules((prev) => prev.filter((r) => r.id !== id));
      setSuccessBanner(`Notification rule "${name}" deleted.`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to delete rule.';
      setError(msg);
    }
  };

  const openEditRule = (rule: NotificationRule) => {
    setEditingRule(rule);
    setNewRuleName(rule.name);
    setNewRuleSeverity(rule.severity_threshold);
    setNewRuleEmails(rule.recipient_emails.join(', '));
    setRuleEnabled(rule.enabled);
  };

  const handleSaveRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingRule || !newRuleName) return;
    try {
      const updated = await api.notifications.updateRule(editingRule.id, {
        name: newRuleName,
        severity_threshold: newRuleSeverity,
        recipient_emails: newRuleEmails
          .split(',')
          .map((email) => email.trim())
          .filter(Boolean),
        enabled: ruleEnabled,
      });
      setRules((prev) => prev.map((rule) => (rule.id === updated.id ? updated : rule)));
      setEditingRule(null);
      setSuccessBanner(`Notification rule "${updated.name}" updated.`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update rule.');
    }
  };

  const handleToggleRule = async (rule: NotificationRule) => {
    try {
      const updated = await api.notifications.updateRule(rule.id, {
        name: rule.name,
        severity_threshold: rule.severity_threshold,
        recipient_emails: rule.recipient_emails,
        enabled: !rule.enabled,
      });
      setRules((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update rule.');
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] font-sans text-slate-900 pb-16">
      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-6">
        {/* Banner Messages */}
        {successBanner && (
          <div className="rounded-lg bg-emerald-50 p-4 border border-emerald-200 text-xs text-emerald-950 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <p className="font-medium">{successBanner}</p>
            </div>
            <button
              onClick={() => setSuccessBanner(null)}
              className="text-emerald-700 hover:text-emerald-900 p-1 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-rose-50 p-4 border border-rose-200 text-xs text-rose-950 flex items-center justify-between shadow-xs">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <p className="font-medium">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-rose-700 hover:text-rose-900 p-1 cursor-pointer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-white rounded-xl p-6 md:p-8 border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-0.5 rounded">
                MOCK
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-0.5 rounded">
                <Bell className="w-3 h-3 text-blue-600" />
                Alert Routing
              </span>
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
              Notification & Alert Rule Center
            </h1>
            <p className="text-xs text-slate-600 max-w-2xl leading-relaxed">
              Configure your personal regulatory email digest frequency and define system-wide
              critical alert escalation rules.
            </p>
          </div>

          {/* Tab Selector */}
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold shrink-0">
            <button
              onClick={() => setActiveTab('preferences')}
              className={`px-3.5 py-1.5 rounded-md transition cursor-pointer ${
                activeTab === 'preferences'
                  ? 'bg-white text-blue-900 shadow-xs'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              My Subscription Preferences
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('rules')}
                className={`px-3.5 py-1.5 rounded-md transition cursor-pointer ${
                  activeTab === 'rules'
                    ? 'bg-white text-blue-900 shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                Admin Alert Rules
              </button>
            )}
          </div>
        </div>

        {/* Tab 1: User Preferences */}
        {activeTab === 'preferences' && (
          <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] p-6 md:p-8 space-y-6">
            {loading ? (
              <div className="p-16 text-center">
                <div className="inline-block w-8 h-8 border-3 border-blue-700 border-t-transparent rounded-full animate-spin mb-3"></div>
                <p className="text-slate-600 text-xs font-semibold">Loading preferences...</p>
              </div>
            ) : (
              <form onSubmit={handleSavePreferences} className="space-y-6 text-xs">
                {/* Cadence Selection */}
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900 mb-1 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-blue-700" /> Regulatory Digest Cadence
                  </h3>
                  <p className="text-slate-500 mb-3 text-xs">
                    Choose how often you would like to receive an executive summary digest of newly
                    ingested regulatory signals.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {(['daily', 'weekly', 'off'] as const).map((freq) => (
                      <label
                        key={freq}
                        className={`p-4 rounded-xl border flex flex-col justify-between cursor-pointer transition ${
                          preferences.digest_frequency === freq
                            ? 'bg-blue-50/60 border-blue-300 ring-2 ring-blue-600/20'
                            : 'bg-slate-50/70 border-slate-200 hover:bg-slate-100/80'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-slate-900 capitalize text-sm">
                            {freq === 'off' ? 'Digest Disabled' : `${freq} Digest`}
                          </span>
                          <input
                            type="radio"
                            name="digest_frequency"
                            value={freq}
                            checked={preferences.digest_frequency === freq}
                            onChange={() =>
                              setPreferences((prev) => ({ ...prev, digest_frequency: freq }))
                            }
                            className="text-blue-700 focus:ring-blue-600"
                          />
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2">
                          {freq === 'daily'
                            ? 'Daily morning briefing at 08:00 UTC with top signals.'
                            : freq === 'weekly'
                              ? 'Comprehensive Monday weekly recap and trend digest.'
                              : 'No periodic digest emails will be sent.'}
                        </p>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Critical Alerts Toggle */}
                <div className="pt-4 border-t border-slate-100">
                  <div className="flex items-center justify-between bg-amber-50/70 border border-amber-200/80 p-4 rounded-xl">
                    <div className="flex items-start gap-3">
                      <Zap className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-amber-950 text-sm">
                          Immediate Critical Priority Alerts
                        </h4>
                        <p className="text-[11px] text-amber-900/80 mt-0.5">
                          Receive instant push notifications and emails when a Critical Urgency
                          Class I Recall or Warning Letter is detected.
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
                      <input
                        type="checkbox"
                        checked={preferences.critical_alerts_enabled}
                        onChange={(e) =>
                          setPreferences((prev) => ({
                            ...prev,
                            critical_alerts_enabled: e.target.checked,
                          }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-700"></div>
                    </label>
                  </div>
                </div>

                {/* Market Subscriptions */}
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-display text-base font-semibold text-slate-900 mb-1">
                    Subscribed Geographic Markets
                  </h3>
                  <p className="text-slate-500 mb-3 text-xs">
                    Only signals concerning your selected regulatory jurisdictions will be included
                    in your alerts.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {AVAILABLE_MARKETS.map((market) => {
                      const selected = preferences.preferred_markets.includes(market);
                      return (
                        <button
                          type="button"
                          key={market}
                          onClick={() => handleToggleMarket(market)}
                          className={`px-3.5 py-1.5 rounded-lg border text-xs font-semibold transition cursor-pointer active:scale-[0.98] ${
                            selected
                              ? 'bg-blue-700 text-white border-blue-700 shadow-xs'
                              : 'bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100'
                          }`}
                        >
                          {market} {selected && '✓'}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Product Line Subscriptions */}
                <div className="pt-4 border-t border-slate-100">
                  <h3 className="font-display text-base font-semibold text-slate-900 mb-1">
                    Product Line Scope Filtering
                  </h3>
                  <p className="text-slate-500 mb-3 text-xs">
                    Select Spacelabs product lines (and the specific devices beneath each) that are
                    relevant to your team. Leave empty to monitor all portfolio signals.
                  </p>
                  <div className="space-y-3">
                    {PRODUCT_LINES.map((line) => {
                      const lineSelected = preferences.preferred_product_lines.includes(line.key);
                      const devices = PRODUCT_LINE_TO_DEVICES[line.key];
                      return (
                        <div
                          key={line.key}
                          className="border border-slate-200 rounded-lg p-3 bg-slate-50/40"
                        >
                          <button
                            type="button"
                            onClick={() => handleToggleProductLine(line.key)}
                            className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs font-semibold transition cursor-pointer ${
                              lineSelected
                                ? 'bg-blue-700 text-white shadow-xs'
                                : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'
                            }`}
                          >
                            <span>
                              {line.label}{' '}
                              <span className="text-[10px] font-medium opacity-80 ml-1">
                                ({devices.length} device{devices.length === 1 ? '' : 's'})
                              </span>
                            </span>
                            <span>{lineSelected ? '✓' : '+'}</span>
                          </button>
                          {lineSelected && (
                            <div className="flex flex-wrap gap-1.5 mt-2 pl-2">
                              {devices.map((device) => {
                                const deviceSelected =
                                  preferences.preferred_products.includes(device);
                                return (
                                  <button
                                    type="button"
                                    key={device}
                                    onClick={() => handleToggleProduct(device)}
                                    className={`px-2.5 py-1 rounded-md border text-[11px] font-medium transition cursor-pointer ${
                                      deviceSelected
                                        ? 'bg-blue-50 text-blue-900 border-blue-200'
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100'
                                    }`}
                                  >
                                    {device}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-slate-400 text-[11px] mt-3">
                    Selected lines:{' '}
                    {preferences.preferred_product_lines.length === 0
                      ? 'None (all signals)'
                      : preferences.preferred_product_lines.map(getProductLineLabel).join(', ')}
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 pt-4 border-t border-slate-100">
                  <button
                    type="button"
                    onClick={handleSendTestEmail}
                    disabled={sendingTest}
                    className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-4 py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-xs cursor-pointer disabled:opacity-50 active:scale-[0.98]"
                  >
                    <Mail className="w-4 h-4 text-blue-700" />
                    <span>
                      {sendingTest ? 'Sending Sample Alert...' : 'Send Test Alert Notification'}
                    </span>
                  </button>

                  <button
                    type="submit"
                    disabled={saving}
                    className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-5 py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-xs cursor-pointer disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{saving ? 'Saving Preferences...' : 'Save Preferences'}</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Tab 2: Admin Alert Rules */}
        {activeTab === 'rules' && isAdmin && (
          <div className="bg-white rounded-xl border border-slate-200/90 shadow-[0_1px_3px_rgba(15,23,42,0.03)] p-6 md:p-8 space-y-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b border-slate-100 pb-4">
              <div>
                <h3 className="font-display text-base font-semibold text-slate-900 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-blue-700" /> System-Wide Alert Escalation Rules
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Configure distribution lists that automatically receive broadcast notifications
                  when incoming regulatory events match severity thresholds.
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => handleTriggerDigest('daily')}
                  disabled={triggeringDigest}
                  className="border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-3.5 py-2 rounded-lg transition flex items-center gap-1.5 shadow-xs cursor-pointer disabled:opacity-50 active:scale-[0.98]"
                  title="Manually trigger and dispatch daily digest emails"
                >
                  <Mail className="w-4 h-4 text-blue-700" />
                  <span>{triggeringDigest ? 'Dispatching...' : 'Run Daily Digest'}</span>
                </button>
                <button
                  onClick={() => {
                    setEditingRule(null);
                    setNewRuleName('');
                    setNewRuleSeverity('critical');
                    setNewRuleEmails('');
                    setRuleEnabled(true);
                    setShowAddRuleModal(true);
                  }}
                  className="bg-blue-700 hover:bg-blue-800 active:scale-[0.98] text-white text-xs font-semibold px-3.5 py-2 rounded-lg transition flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <Plus className="w-4 h-4" /> Add Rule
                </button>
              </div>
            </div>

            {rules.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-xs">
                No notification rules configured. Click &quot;Add Rule&quot; to define escalation
                routing.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-[11px] font-semibold text-slate-600 uppercase tracking-wider">
                      <th className="py-3 px-4">Rule Name</th>
                      <th className="py-3 px-4">Severity Threshold</th>
                      <th className="py-3 px-4">Recipient Distribution List</th>
                      <th className="py-3 px-4">Status</th>
                      <th className="py-3 px-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-slate-50 transition-colors">
                        <td className="py-3.5 px-4 font-semibold text-slate-900">{rule.name}</td>
                        <td className="py-3.5 px-4">
                          <span className="bg-rose-50 text-rose-800 text-[10px] font-bold uppercase px-2.5 py-0.5 rounded border border-rose-200">
                            {rule.severity_threshold}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-slate-600 font-mono text-[11px]">
                          {rule.recipient_emails.join(', ') || 'No recipients'}
                        </td>
                        <td className="py-3.5 px-4">
                          <span
                            className={`${rule.enabled ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'} text-[10px] font-bold uppercase px-2.5 py-0.5 rounded border`}
                          >
                            {rule.enabled ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="py-3.5 px-4 text-right">
                          <button
                            onClick={() => handleToggleRule(rule)}
                            className="text-slate-600 hover:text-blue-700 p-1.5 rounded-lg hover:bg-blue-50 transition cursor-pointer"
                            title={rule.enabled ? 'Disable Rule' : 'Enable Rule'}
                          >
                            <Zap className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditRule(rule)}
                            className="text-blue-700 hover:text-blue-900 p-1.5 rounded-lg hover:bg-blue-50 transition cursor-pointer"
                            title="Edit Rule"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule.id, rule.name)}
                            className="text-rose-700 hover:text-rose-900 p-1.5 rounded-lg hover:bg-rose-50 transition cursor-pointer"
                            title="Delete Rule"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Add Notification Rule Modal */}
      {(showAddRuleModal || editingRule) && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 border border-slate-200 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2">
                {editingRule ? (
                  <Pencil className="w-4 h-4 text-blue-700" />
                ) : (
                  <Plus className="w-4 h-4 text-blue-700" />
                )}
                {editingRule ? 'Edit Escalation Rule' : 'Create Escalation Rule'}
              </h3>
              <button
                onClick={() => {
                  setShowAddRuleModal(false);
                  setEditingRule(null);
                }}
                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form
              onSubmit={editingRule ? handleSaveRule : handleCreateRule}
              className="space-y-4 text-xs"
            >
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Rule Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Critical Cardiac Recall Alert"
                  value={newRuleName}
                  onChange={(e) => setNewRuleName(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <label className="flex items-center gap-2 text-slate-700 font-semibold">
                <input
                  type="checkbox"
                  checked={ruleEnabled}
                  onChange={(e) => setRuleEnabled(e.target.checked)}
                  className="h-4 w-4 accent-blue-700"
                />
                Rule enabled
              </label>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Severity Threshold
                </label>
                <select
                  value={newRuleSeverity}
                  onChange={(e) => setNewRuleSeverity(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-600 font-medium"
                >
                  <option value="critical">Critical Priority Only</option>
                  <option value="high">High & Critical Priority</option>
                  <option value="medium">Medium, High & Critical</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">
                  Recipient Distribution Emails (comma-separated)
                </label>
                <input
                  type="text"
                  required
                  placeholder="raqa-lead@spacelabs.com, compliance@spacelabs.com"
                  value={newRuleEmails}
                  onChange={(e) => setNewRuleEmails(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg p-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddRuleModal(false);
                    setEditingRule(null);
                  }}
                  className="px-4 py-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-xs font-semibold text-white bg-blue-700 hover:bg-blue-800 active:scale-[0.98] rounded-lg shadow-xs transition cursor-pointer"
                >
                  {editingRule ? 'Save Changes' : 'Create Rule'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
